import { Button } from "@client/components/ui";
import { BasketForm } from "@client/features/basket/BasketForm";
import { BasketTabs } from "@client/features/basket/BasketTabs";
import { BuyTable } from "@client/features/basket/BuyTable";
import { LadderChart } from "@client/features/basket/LadderChart";
import { RelativeStrength } from "@client/features/basket/RelativeStrength";
import { SellTable } from "@client/features/basket/SellTable";
import { SummaryTiles } from "@client/features/basket/SummaryTiles";
import { TokenContext } from "@client/features/basket/TokenContext";
import { useBasketDraft } from "@client/features/basket/useBasketDraft";
import { round } from "@client/features/basket/util";
import { indicatorValuesFromOverview } from "@client/features/pulpit/lib";
import { api } from "@client/lib/api";
import {
  basketQuery,
  basketsQuery,
  candlesQuery,
  invalidateAfterBasketChange,
  overviewQuery,
  settingsQuery,
  tokensQuery,
  topCoinsQuery,
} from "@client/lib/queries";
import { useToast } from "@client/lib/toast";
import { calcBasket } from "@shared/basket";
import { evaluateCondition, formatCondition } from "@shared/conditions";
import { type Basket, DEFAULT_SETTINGS } from "@shared/contracts";
import { count, pct, price, usd } from "@shared/format";
import { isFloorBroken } from "@shared/ladder";
import { analyseTokenomics } from "@shared/tokenomics";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

export const Route = createFileRoute("/koszyki/$basketId")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(basketQuery(params.basketId)),
  component: BasketPage,
});

function BasketPage() {
  const { basketId } = Route.useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const { data: basket } = useSuspenseQuery(basketQuery(basketId));
  const tokensQ = useQuery(tokensQuery());
  const basketsQ = useQuery(basketsQuery());
  const candlesQ = useQuery(candlesQuery(basket.tokenId));
  const settingsQ = useQuery(settingsQuery());
  const overviewQ = useQuery(overviewQuery());
  const topQ = useQuery(topCoinsQuery());

  const { draft, update } = useBasketDraft(basket);

  const tokens = tokensQ.data ?? [];
  const baskets = basketsQ.data ?? [];
  const tokensById = useMemo(() => new Map(tokens.map((t) => [t.id, t] as const)), [tokens]);
  const token = tokensById.get(basket.tokenId) ?? null;
  const currentPrice = token?.priceUsd ?? null;
  const candles = candlesQ.data?.candles ?? [];
  const settings = settingsQ.data ?? DEFAULT_SETTINGS;
  const fx = overviewQ.data?.fx ?? null;

  const merged: Basket = { ...basket, ...draft };
  const result = calcBasket(merged);
  const floorBroken = isFloorBroken(merged, currentPrice);

  const tokenomics = useMemo(() => {
    if (!token) return null;
    const total3 = overviewQ.data?.indicators.find((i) => i.key === "total3_usd")?.value ?? null;
    const tps = result.sells.map((s) => ({ index: s.index, mult: s.mult, price: s.price, qty: s.qty }));
    return analyseTokenomics(
      token,
      tps,
      topQ.data?.coins ?? [],
      total3,
      new Date().toISOString().slice(0, 10),
    );
  }, [token, result.sells, topQ.data, overviewQ.data]);

  const indicatorValues = useMemo(
    () => (overviewQ.data ? indicatorValuesFromOverview(overviewQ.data.indicators) : {}),
    [overviewQ.data],
  );

  const indicatorMet = useMemo(
    () => (draft.startIndicator ? evaluateCondition(draft.startIndicator, indicatorValues) : null),
    [draft.startIndicator, indicatorValues],
  );

  const exitTriggerMet = useMemo(
    () => (merged.exitTrigger ? evaluateCondition(merged.exitTrigger, indicatorValues) : null),
    [merged.exitTrigger, indicatorValues],
  );

  const trailingHit =
    merged.trailingPct != null &&
    basket.trailingHigh != null &&
    currentPrice != null &&
    currentPrice <= basket.trailingHigh * (1 - merged.trailingPct / 100);

  const showExitBanner = basket.status === "active" && (exitTriggerMet === true || trailingHit);
  // single mode derives its only TP from `target`, which the server freezes once that TP is executed
  const canLowerTp = draft.exitMode === "ladder" || basket.sellFills.length === 0;

  function lowerRemainingTp(k: number) {
    const executedIndexes = new Set(basket.sellFills.map((f) => f.tpIndex));
    if (draft.exitMode === "ladder") {
      // lower each pending level, but never below the level before it (the ladder stays monotonic)
      const next: typeof draft.tp = [];
      for (const [i, lvl] of draft.tp.entries()) {
        const prevM = next[i - 1]?.m ?? 1;
        const lowered = Math.max(1.01, round(lvl.m * (1 - k), 2));
        next.push(executedIndexes.has(i) ? lvl : { ...lvl, m: Math.max(lowered, round(prevM * 1.05, 2)) });
      }
      update({ tpTemplate: "manual", tp: next }, { immediate: true });
    } else {
      update({ target: Math.max(1.01, round(draft.target * (1 - k), 2)) }, { immediate: true });
    }
  }

  const startMutation = useMutation({
    mutationFn: () => api.baskets.start(basket.id),
    onSuccess: (result_) => {
      qc.setQueryData(basketQuery(basket.id).queryKey, result_);
      void invalidateAfterBasketChange(qc);
      toast.success("Koszyk uruchomiony.");
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Nie udało się uruchomić koszyka."),
  });

  const closeMutation = useMutation({
    mutationFn: () => api.baskets.close(basket.id),
    onSuccess: (result_) => {
      qc.setQueryData(basketQuery(basket.id).queryKey, result_);
      void invalidateAfterBasketChange(qc);
      toast.success("Koszyk zamknięty.");
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : "Nie udało się zamknąć koszyka."),
  });

  const removeMutation = useMutation({
    mutationFn: () => api.baskets.remove(basket.id),
    onSuccess: async () => {
      await invalidateAfterBasketChange(qc);
      await navigate({ to: "/koszyki" });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Nie udało się usunąć koszyka."),
  });

  return (
    <section className="flex flex-col gap-4 p-3 md:p-6">
      <BasketTabs baskets={baskets} tokensById={tokensById} currentBasketId={basket.id} />

      {floorBroken && (
        <div
          className="flex items-center gap-3 rounded-md border px-4 py-3 text-[13px]"
          style={{
            background: "rgba(240,82,82,.12)",
            borderColor: "var(--color-red-700)",
            color: "var(--color-red-300)",
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="shrink-0"
          >
            <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
          </svg>
          <span>
            <strong>Podłoga przebita.</strong> Cena {price(currentPrice)} jest poniżej podłogi{" "}
            {price(merged.floor)}. Teza do rewizji — kolejne szczeble są zablokowane, nie dokupuj „bo tanio”.
          </span>
        </div>
      )}

      {showExitBanner && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-md border px-4 py-3 text-[13px]"
          style={{ background: "rgba(14,159,110,.12)", borderColor: "var(--color-sell)" }}
        >
          <span className="flex-1">
            <strong>Sygnał wyjścia.</strong>{" "}
            {trailingHit && merged.trailingPct != null && basket.trailingHigh != null
              ? `Trailing stop: cena ${price(currentPrice)} jest ${pct(-merged.trailingPct)} poniżej szczytu ${price(basket.trailingHigh)}`
              : merged.exitTrigger
                ? `Warunek ${formatCondition(merged.exitTrigger)} spełniony`
                : ""}
            . Pozostało {count(result.heldNow)} tok. ≈{" "}
            {usd(currentPrice != null ? result.heldNow * currentPrice : null)}.
          </span>
          {canLowerTp && (
            <div className="flex gap-2">
              <Button size="sm" variant="alt" onClick={() => lowerRemainingTp(0.1)}>
                Obniż pozostałe TP o 10 %
              </Button>
              <Button size="sm" variant="alt" onClick={() => lowerRemainingTp(0.2)}>
                o 20 %
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,272px),1fr))] items-start gap-4 md:gap-5">
        <BasketForm
          basket={basket}
          draft={draft}
          update={update}
          tokens={tokens}
          currentPrice={currentPrice}
          fx={fx}
          result={result}
          tokenomics={tokenomics}
          indicatorMet={indicatorMet}
          onStart={() => startMutation.mutate()}
          onClose={() => closeMutation.mutate()}
          onRemove={() => removeMutation.mutate()}
          starting={startMutation.isPending}
          closing={closeMutation.isPending}
          removing={removeMutation.isPending}
        />

        <div className="flex min-w-0 flex-col gap-4">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,380px),1fr))] items-start gap-4">
            <LadderChart
              symbol={token?.symbol ?? "?"}
              floor={merged.floor}
              budgetUsd={merged.budgetUsd}
              feePct={merged.feePct}
              result={result}
              candles={candles}
              currentPrice={currentPrice}
            />
            <div className="flex min-w-0 flex-col gap-4">
              <BuyTable
                basketId={basket.id}
                result={result}
                buyFills={basket.buyFills}
                budgetUsd={merged.budgetUsd}
              />
              <SellTable
                basketId={basket.id}
                result={result}
                draft={draft}
                update={update}
                tokenomics={tokenomics}
                trailingHigh={basket.trailingHigh}
              />
            </div>
          </div>

          <TokenContext token={token} tokenomics={tokenomics} topDay={topQ.data?.day ?? null} />

          <RelativeStrength
            token={token}
            candles={candles}
            btcUsd={overviewQ.data?.indicators.find((i) => i.key === "btc_usd")?.value ?? null}
            sells={result.sells}
          />

          <SummaryTiles
            result={result}
            budgetUsd={merged.budgetUsd}
            feePct={merged.feePct}
            currentPrice={currentPrice}
            fx={fx}
            plnBeside={settings.plnBeside}
          />
        </div>
      </div>
    </section>
  );
}
